import * as React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { LangFormSchemaValues, langFormSchema } from 'src/features/profile/api/useUpdateUser';
import { languageOptions } from 'src/features/profile/utils/constants';
import { settingsMessages } from 'src/features/profile/utils/messages';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockSubtitle,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/utils/messages';
import { storage } from 'src/utils/storage';
import { LOCALE } from 'src/providers/types';
import SettingsContext from 'src/features/administration/context/SettingsContext';

const ProfileSettingsForm = () => {
    const { formatMessage } = useIntl();
    const lang = storage.getLanguage();

    const settings = React.useContext(SettingsContext);
    const settingsLanguages: LOCALE[] = settings.languages;

    const form = useForm<LangFormSchemaValues>({
        resolver: zodResolver(langFormSchema),
        defaultValues: {
            lang,
        },
    });

    // This is currently a bug in all of our forms: The form is not validating the initial values because the form fields are not mounted yet. This is a workaround to trigger the validation after the form fields are mounted.
    // Another solution is to simply remove the isValid check from the submit button
        React.useEffect(() => {
            void form.trigger();
        }, []);

        const onSubmit = ({ lang }: LangFormSchemaValues) => {
            storage.setLanguage(lang);
            form.reset({ lang });
            window.location.reload();
        };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(settingsMessages.language)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>{formatMessage(settingsMessages.languageSubtitle)}</FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="lang"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Select onValueChange={field.onChange} defaultValue={field.value?.toString()}>
                                        <SelectTrigger>
                                            <SelectValue
                                                placeholder={formatMessage(settingsMessages.changeLanguagePlacholder)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {settingsLanguages?.map((language: LOCALE) => {
                                                return (
                                                    <SelectItem key={language} value={language}>
                                                        {formatMessage(languageOptions[language])}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <Button
                className="sm:place-self-end"
                type="submit"
                disabled={!form.formState.isDirty || !form.formState.isValid}
            >
                {formatMessage(generalMessages.saveChanges)}
            </Button>
        </Form>
    );
};

export default ProfileSettingsForm;
